import React, { useEffect, useRef, useState } from 'react';
import * as s from './styled-variable-multi-picker';

// Picker multi-select pra comparar variaveis no LineChart avancado.
// Recebe lista total de variaveis + array de selecionadas (compareVars)
// + cores ja atribuidas (mapa por ihmname). Limite max de selecoes
// (default 2 — somando com a primaria do chart, total 3 series).
//
// UX:
// - Botao trigger no header mostra "+ Comparar (N)" se ja tem
//   selecoes, senao so "Comparar".
// - Dropdown lista todas as variaveis disponiveis (exceto a primaria).
// - Cada opcao com dot colorido (cor da serie quando selecionada) +
//   nome + unidade.
// - Click adiciona/remove. Quando atingiu max, opcoes nao selecionadas
//   ficam disabled.
// - Click fora fecha o dropdown.
const VariableMultiPicker = ({
  variables = [],
  selectedIhmnames = [],
  excludeIhmnames = [],
  colorMap = {},
  // Set/array dos ihmnames atualmente em loading (fetch em progresso).
  // Quando passado, o dot da opcao vira spinner — feedback visual de
  // 'estou puxando os dados'.
  loadingIhmnames = [],
  max = 2,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Click fora fecha
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Filtra variaveis disponiveis: exclui a primaria + qualquer outra
  // explicitamente excluida, e exige ihmname/name pra render.
  const options = (variables || []).filter(
    (v) =>
      v &&
      v.ihmname &&
      v.name &&
      !excludeIhmnames.includes(v.ihmname)
  );

  const isSelected = (ihmname) => selectedIhmnames.includes(ihmname);
  const atMax = selectedIhmnames.length >= max;

  const togglePick = (ihmname) => {
    if (isSelected(ihmname)) {
      onChange(selectedIhmnames.filter((n) => n !== ihmname));
    } else if (!atMax) {
      onChange([...selectedIhmnames, ihmname]);
    }
  };

  const selectedCount = selectedIhmnames.length;

  return (
    <s.Container ref={containerRef}>
      <s.TriggerButton
        type="button"
        $active={selectedCount > 0}
        onClick={() => setOpen((v) => !v)}
        title={
          selectedCount > 0
            ? `${selectedCount} variável(eis) em comparação`
            : 'Comparar com outras variáveis'
        }
      >
        {/* lucide:layers */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
        comparar
        {selectedCount > 0 && <s.Badge>{selectedCount}</s.Badge>}
      </s.TriggerButton>

      {open && (
        <s.Dropdown>
          <s.DropdownHeader>
            <s.DropdownTitle>Comparar variáveis</s.DropdownTitle>
            <s.DropdownHint>
              {selectedCount}/{max}
            </s.DropdownHint>
          </s.DropdownHeader>
          {options.length === 0 ? (
            <s.Empty>Nenhuma variável adicional disponível.</s.Empty>
          ) : (
            options.map((v) => {
              const sel = isSelected(v.ihmname);
              const disabled = !sel && atMax;
              const color = colorMap[v.ihmname];
              return (
                <s.Option
                  key={v.id || v.ihmname}
                  type="button"
                  $selected={sel}
                  disabled={disabled}
                  onClick={() => togglePick(v.ihmname)}
                  title={
                    disabled
                      ? `Máximo de ${max} variáveis em comparação atingido`
                      : sel
                      ? `Remover ${v.name} da comparação`
                      : `Adicionar ${v.name} à comparação`
                  }
                >
                  {sel && color && loadingIhmnames.includes(v.ihmname) ? (
                    <s.DotSpinner color={color} />
                  ) : sel && color ? (
                    <s.ColorDot color={color} />
                  ) : (
                    <s.ColorDotPlaceholder />
                  )}
                  <s.OptionLabel>{v.name}</s.OptionLabel>
                  {v.range && v.range[0] && v.range[0].unit && (
                    <s.OptionUnit>{v.range[0].unit}</s.OptionUnit>
                  )}
                </s.Option>
              );
            })
          )}
        </s.Dropdown>
      )}
    </s.Container>
  );
};

export default VariableMultiPicker;
